import { ArrayHelpers, FastField, FieldArray, FieldProps, FormikProps, useFormikContext } from 'formik';
import { DeclineIcon, EmailIcon } from '@synergy/theme';
import { EmailType } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment } from 'react';
import { Grid, IconButton } from '@mui/material';
import { PoolButton, PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { EmailFormValues } from '../../models/email-form-values';
import { SubtitleWithIcon } from '../subtitle-with-icon';

type EmailArrayHelpers = ArrayHelpers & {
    form: FormikProps<OverviewEmailFormValues>;
    name: string;
};

interface OverviewEmailFormValues {
    mainEmail: string;
    emails: EmailFormValues[];
}

// The fixed entry's type, standing where the others show a select. The 17/24
// padding it carried was measured against a 38-pixel field; beside a 29-pixel
// one it sat low and 24 pixels off to the right.
const useStyles = makeStyles(() => ({
    static: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 29,
        fontSize: '12.5px',
    },
}));

const emailTypeOptions: SelectOption[] = [
    {
        value: EmailType.Work,
        displayValue: <FormattedMessage id="app.common.work" />,
    },
    {
        value: EmailType.Home,
        displayValue: <FormattedMessage id="app.common.private" />,
    },
];

export const EmailTable = () => {
    const classes = useStyles();
    const intl = useIntl();
    const formik = useFormikContext<OverviewEmailFormValues>();

    const handleNewEmail = (helper: EmailArrayHelpers) => {
        helper.push({ id: new Date().getTime() * -1, type: EmailType.Work, email: '' });
    };

    return (
        <>
            <SubtitleWithIcon
                title={<FormattedMessage id="app.common.email" />}
                icon={<EmailIcon />}
                required={true}
                error={
                    !!formik.touched.emails &&
                    !!formik.errors.emails &&
                    !!formik.touched.mainEmail &&
                    !!formik.errors.mainEmail
                }
            />
            <Grid container={true} spacing={2}>
                <Grid item={true} xs={2}>
                    <PoolTypography
                        typography="body1"
                        component="div"
                        className={classes.static}
                        id="app.common.default"
                    />
                </Grid>
                <Grid item={true} xs={4}>
                    <FastField name={`mainEmail`}>
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextField
                                {...field}
                                required={true}
                                placeholder={intl.formatMessage({
                                    id: 'app.contactsAndOrganizations.detail.exampleEmail',
                                })}
                                fullWidth={true}
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                value={field.value}
                            />
                        )}
                    </FastField>
                </Grid>
                <Grid item={true} xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => formik.setFieldValue('mainEmail', '')}>
                        <DeclineIcon />
                    </IconButton>
                </Grid>

                <FieldArray name="emails">
                    {(helper: EmailArrayHelpers) => {
                        return (
                            <>
                                {helper.form.values.emails.map((email, index) => (
                                    <Fragment key={email.id}>
                                        <Grid item={true} xs={2}>
                                            <FastField name={`emails.${index}.type`}>
                                                {({ field, form }: FieldProps<EmailType>) => (
                                                    <PoolSelect
                                                        fullWidth={true}
                                                        disallowDefaultValue={true}
                                                        onValueChange={value => form.setFieldValue(field.name, value)}
                                                        value={field.value}
                                                        name={field.name}
                                                        noSorting={true}
                                                        options={emailTypeOptions}
                                                    />
                                                )}
                                            </FastField>
                                        </Grid>
                                        <Grid item={true} xs={4}>
                                            <FastField name={`emails.${index}.email`}>
                                                {({ field, form, meta }: FieldProps<string>) => (
                                                    <PoolTextField
                                                        {...field}
                                                        required={true}
                                                        placeholder={intl.formatMessage({
                                                            id: 'app.contactsAndOrganizations.detail.exampleEmail',
                                                        })}
                                                        fullWidth={true}
                                                        error={!!meta.touched && !!meta.error}
                                                        helperText={
                                                            meta.touched && meta.error ? (
                                                                <FormattedMessage id={meta.error} />
                                                            ) : null
                                                        }
                                                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                        value={field.value}
                                                    />
                                                )}
                                            </FastField>
                                        </Grid>
                                        <Grid item={true} xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton onClick={() => helper.remove(index)}>
                                                <DeclineIcon />
                                            </IconButton>
                                        </Grid>
                                    </Fragment>
                                ))}

                                <Grid item={true} xs={12}>
                                    <PoolButton
                                        styleType="thirdLevel"
                                        startIcon={<AddIcon color="primary" />}
                                        onClick={() => handleNewEmail(helper)}
                                    >
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.addEmail" />
                                    </PoolButton>
                                </Grid>
                            </>
                        );
                    }}
                </FieldArray>
            </Grid>
        </>
    );
};
